import React, { useState, useRef, useEffect } from "react";

// Modules

// App
import { presenterNotesEnabled, presenterAudioEnabled } from "../../../config";
import { useSlideContext } from "../../../contexts/slideContext";
import { useWindowSize } from "../../../partials/windowSize";
import AnalyseSlideWithAi from "../../../partials/ai/AnalyseSlide";

// UI components
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {
  Button,
  FormControlLabel,
  Checkbox,
  Tooltip,
  IconButton,
  Box,
  Typography,
  TextField,
  SvgIcon,
  Popover,
} from "@mui/material";
import {
  PlayArrow,
  Settings,
  KeyboardBackspace,
  CheckOutlined,
  WidgetsOutlined,
  LockOutlined,
  LockOpenOutlined,
} from "@mui/icons-material";
import { ReactComponent as MusicNoteIcon } from "../../../assets/music-notes.svg";
import UnhideIcon from "@mui/icons-material/VisibilityOutlined";
import HideIcon from "@mui/icons-material/VisibilityOffOutlined";
import NotesIcon from "@mui/icons-material/TextSnippetOutlined";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import BackgroundIcon from "@mui/icons-material/WallpaperOutlined";
import ToolbarBtn from "./toolbarBtn";

export default function Toolbar(props) {
  const backTooltipTarget = useRef(null);
  const viewTooltipTarget = useRef(null);
  const settingsTooltipTarget = useRef(null);
  const deleteTooltipTarget = useRef(null);
  const presentationAudioTooltipTarget = useRef(null);

  const [index, setIndex] = useState(0);
  const [slide, setSlide] = useState(null);
  const [isReadOnly, setReadOnly] = useState(false);
  const [isCorrupt, setCorrupt] = useState(false);
  const [blockToolsAnchor, setBlockToolsAnchor] = useState(null);

  const permissions = props.permissions;

  const {
    activeSlideTitle,
    activeSlideLayout,
    triggerToggleGrid,
    triggerModifySlideTitle,
    hasSlideAudio,
    triggerGroup,
    triggerUngroup,
    // selectModeStatus,
    blockOutlines,
    triggerBlockOutlines,
    showBlockTree,
    setShowBlockTree,
    activeSlideGrid,
    triggerBlockDuplicate,
    activeSelectedBlocks,
    activeSelectedGroups,
    activeCustomLayoutLock,
    triggerLockSlideLayout,
  } = useSlideContext();

  const { width } = useWindowSize();

  useEffect(() => {
    setIndex(props.indexh);
  }, [props.indexh]);

  useEffect(() => {
    if (props.presentation && props.presentation?.slides?.length > 0) {
      props.presentation.slides.forEach((slide, slideIndex) => {
        if (index === slideIndex) {
          setSlide(slide);

          if (slide.initial_presentation !== props.presentation.id) {
            setReadOnly(true);
          } else {
            setReadOnly(false);
          }
        }
      });
    }
  }, [index, props.presentation, permissions, props.userAuth]);

  useEffect(() => {
    if (slide?.id) {
      if (props.corruptSlides.length) {
        if (props.corruptSlides.includes(slide.id)) {
          setCorrupt(true);
        } else {
          setCorrupt(false);
        }
      }
    }
  }, [slide, props.corruptSlides]);

  return (
    <div className="ps-toolbar">
      <Container fluid>
        <Row className="align-items-center justify-content-between toolbar-row first-row">
          <Col xs={"auto"} lg={6} className="ls">
            <Row className="align-items-center">
              <Col xs="auto">
                <Tooltip
                  id="back-tooltip"
                  title="Back to presentations"
                  placement="bottom"
                >
                  <IconButton
                    ref={backTooltipTarget}
                    variant="circular"
                    owner="admin"
                    className="d-flex align-items-center"
                    onClick={() => {
                      props.goBack();
                    }}
                    color="white"
                  >
                    <KeyboardBackspace />
                  </IconButton>
                </Tooltip>
              </Col>

              {!props.isError && (
                <Col xs="auto">
                  <Tooltip
                    id="settings"
                    title="Presentation settings"
                    placement="bottom"
                  >
                    <IconButton
                      ref={settingsTooltipTarget}
                      variant="circular"
                      owner="admin"
                      className="d-flex align-items-center"
                      onClick={() => {
                        props.onPresentationModal();
                      }}
                      color="white"
                    >
                      <Settings />
                    </IconButton>
                  </Tooltip>
                </Col>
              )}

              {!props.isError && (
                <>
                  {presenterAudioEnabled &&
                    permissions.access_audio_features && (
                      <Col xs="auto">
                        <Tooltip
                          id="presentation-audio-settings"
                          title="Presentation audio"
                          placement="bottom"
                        >
                          <IconButton
                            ref={presentationAudioTooltipTarget}
                            id="presentation-audio-settings-button"
                            variant="circular"
                            owner="admin"
                            className={`d-flex align-items-center ${props.hasPresentationAudio ? "has-presentation-audio" : ""}`}
                            onClick={() => {
                              props.onPresentationAudioModal();
                            }}
                            color="white"
                          >
                            <MusicNoteIcon
                              style={{
                                height: "20px",
                                width: "auto",
                                padding: "1px",
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Col>
                    )}
                </>
              )}

              <Col xs="auto">
                {props?.presentation?.slides?.length > 0 && (
                  <div className="d-flex align-items-center">
                    {width >= 992 ? (
                      <Button
                        variant="circular"
                        owner="admin"
                        ref={viewTooltipTarget}
                        className="preview-btn d-flex align-items-center"
                        // className='d-flex align-items-center'
                        onClick={() => {
                          window.open(
                            `/presentations/preview/${props?.presentation?.id}`,
                            "_blank",
                          );
                        }}
                        startIcon={<PlayArrow fontSize={"small"} />}
                      >
                        <Typography variant="subtitle2" owner="admin">
                          Preview
                        </Typography>
                      </Button>
                    ) : (
                      <Tooltip
                        id="presentation-preview"
                        title="Preview presentation"
                        placement="bottom"
                      >
                        <IconButton
                          ref={presentationAudioTooltipTarget}
                          variant="circular"
                          owner="admin"
                          className="d-flex align-items-center"
                          onClick={() => {
                            window.open(
                              `/presentations/preview/${props?.presentation?.id}`,
                              "_blank",
                            );
                          }}
                          color="white"
                        >
                          <PlayArrow
                            style={{
                              height: "20px",
                              width: "auto",
                              padding: "1px",
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                )}
              </Col>
            </Row>
          </Col>
          <Col xs={"auto"} lg={6} className="rs">
            {!props.isError && (
              <Row className="align-items-center justify-content-end">
                {!isReadOnly && permissions.access_ai_analyse && (
                  <Col xs="auto" className="me-2 pe-1">
                    <AnalyseSlideWithAi
                      slide={slide}
                      presentation={props?.presentation}
                      slideLayout={activeSlideLayout}
                      csrf_token={props.csrf_token}
                      refreshPresentation={props.refreshPresentation}
                    />
                  </Col>
                )}
                <Col xs="auto" className="d-none d-lg-flex">
                  <Typography
                    owner="admin"
                    variant="subtitle2"
                    textTransform={"none"}
                    color={"adminPrimary.main"}
                  >
                    Slide title
                  </Typography>
                </Col>
                <Col xs={"auto"} className="pe-0">
                  <Tooltip
                    id="slide-title-input-tooltip"
                    title={
                      isReadOnly
                        ? "This slide is read only. Slide title can not be changed"
                        : ""
                    }
                    placement="bottom"
                  >
                    <TextField
                      className="toolbar-slide-title-input"
                      InputLabelProps={{ shrink: width >= 992 ? false : true }}
                      InputProps={{ placeholder: "Add a name to your slide" }}
                      disabled={isReadOnly}
                      value={activeSlideTitle}
                      variant="outlined"
                      label={width >= 992 ? "" : "Slide title"}
                      onChange={(e) => {
                        triggerModifySlideTitle(e.target.value);
                        // setActiveSlideTitle(e.target.value);
                        // setModifiedSlideTitle(true);
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "3px 5px",
                          fontSize: "0.75rem",
                          background: "#ffffff",
                        },
                      }}
                    ></TextField>
                  </Tooltip>
                </Col>
              </Row>
            )}
          </Col>
        </Row>

        {!isCorrupt && (
          <>
            <Row className="align-items-center toolbar-row second-row">
              <Col sm={12} md={4} xxl={3} className="ls">
                <Tooltip
                  id="presentation-title-tooltip"
                  title={props?.presentation?.title}
                  placement="bottom"
                >
                  <Typography
                    className="ps-title"
                    variant="subtitle2"
                    owner="admin"
                    color="adminText.dark"
                    sx={{
                      width: `${
                        width >= 1400
                          ? (width - 260) * 0.25
                          : (width - 260) * 0.3333
                      }px`,
                    }}
                  >
                    #{props?.presentation?.id}: {props?.presentation?.title}
                  </Typography>
                </Tooltip>
              </Col>
              {!props.isError && (
                <Col sm={12} md={8} xxl={9} className="rs">
                  <Row className="align-items-center justify-content-end">
                    {!isReadOnly && (
                      <>
                        <Popover
                          className=""
                          anchorEl={blockToolsAnchor}
                          open={Boolean(blockToolsAnchor)}
                          onClose={() => setBlockToolsAnchor(null)}
                          slotProps={{
                            paper: {
                              className:
                                "toolbar-block-tools-popover py-1 px-2",
                              sx: {
                                "& .bx": { p: 0, m: 0 },
                                "& .MuiButton-root": {
                                  p: "0 15px 0 5px",
                                  m: 0,
                                },
                                "& .MuiButton-startIcon": {
                                  mr: "4px",
                                  ml: "-5px",
                                },
                                "& > .col-auto": { p: 0, m: 0 },
                              },
                            },
                          }}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                        >
                          {activeSlideLayout === "custom" && (
                            <>
                              <ToolbarBtn
                                btnClassName="w-100 d-flex justify-content-start"
                                tooltipId="slide-grid-tooltip"
                                tooltipTitle={`${activeSlideGrid ? "Hide" : "Show"} grid`}
                                tooltipPlacement="right"
                                onClick={() => triggerToggleGrid()}
                                btnLabel="Grid"
                                startIconEnabled={true}
                                buttonStartIcon={
                                  activeSlideGrid ? (
                                    <CheckOutlined sx={{ width: 18 }} />
                                  ) : null
                                }
                              />
                            </>
                          )}
                          <ToolbarBtn
                            btnClassName="w-100 d-flex justify-content-start"
                            tooltipId="slide-outline-tooltip"
                            tooltipTitle={`${blockOutlines ? "Hide" : "Show"} block outlines`}
                            tooltipPlacement="right"
                            onClick={() => triggerBlockOutlines()}
                            btnLabel="Outlines"
                            startIconEnabled={true}
                            buttonStartIcon={
                              blockOutlines ? (
                                <CheckOutlined sx={{ width: 18 }} />
                              ) : null
                            }
                          />
                          {activeSlideLayout === "custom" && (
                            <>
                              <ToolbarBtn
                                btnClassName="w-100 d-flex justify-content-start"
                                tooltipId="slide-outline-tooltip"
                                tooltipTitle={`${showBlockTree ? "Hide" : "Show"} block tree`}
                                tooltipPlacement="right"
                                onClick={() =>
                                  setShowBlockTree((value) => !value)
                                }
                                btnLabel="Tree"
                                startIconEnabled={true}
                                buttonStartIcon={
                                  showBlockTree ? (
                                    <CheckOutlined sx={{ width: 18 }} />
                                  ) : null
                                }
                              />
                              <hr className="my-1" />
                              <ToolbarBtn
                                btnClassName="w-100 d-flex justify-content-start"
                                tooltipId="slide-group-tooltip"
                                tooltipTitle="Group selected blocks"
                                tooltipPlacement="right"
                                btnDisabled={activeSelectedBlocks.size <= 1}
                                onClick={() => {
                                  triggerGroup();
                                  setBlockToolsAnchor(null);
                                }}
                                btnLabel="Group"
                                startIconEnabled={true}
                              />
                              <ToolbarBtn
                                btnClassName="w-100 d-flex justify-content-start"
                                tooltipId="slide-ungroup-tooltip"
                                tooltipTitle="Ungroup selected blocks"
                                tooltipPlacement="right"
                                btnDisabled={activeSelectedGroups.size === 0}
                                onClick={() => {
                                  triggerUngroup();
                                  setBlockToolsAnchor(null);
                                }}
                                btnLabel="Ungroup"
                                startIconEnabled={true}
                              />
                              <ToolbarBtn
                                btnClassName="w-100 d-flex justify-content-start"
                                tooltipId="slide-duplicate-tooltip"
                                tooltipTitle="Duplicate selected blocks"
                                tooltipPlacement="right"
                                btnDisabled={activeSelectedBlocks.size === 0}
                                onClick={() => {
                                  triggerBlockDuplicate();
                                  setBlockToolsAnchor(null);
                                }}
                                btnLabel="Duplicate"
                                startIconEnabled={true}
                              />
                            </>
                          )}
                          {/* <ToolbarBtn
                            tooltipId="slide-select-tooltip"
                            tooltipTitle="Multiple block selection"
                            btnClassName={
                              selectModeStatus ? "select-mode-on" : ""
                            }
                            btnId="block-selection-toggle"
                            onClick={() => triggerToggleSelect()}
                            icon={
                              <Grid4x4Icon
                                sx={{ width: "20px", height: "20px" }}
                              />
                            }
                            btnLabel="Select"
                          /> */}
                        </Popover>
                        {activeSlideLayout === "custom" && (
                          <ToolbarBtn
                            width={width}
                            btnId={"slide-lock-btn"}
                            btnClassName={
                              activeCustomLayoutLock ? "slide-locked" : ""
                            }
                            tooltipId="slide-outline-tooltip"
                            tooltipTitle={`${activeCustomLayoutLock ? "Unlock" : "Lock"} slide layout`}
                            onClick={() => triggerLockSlideLayout()}
                            icon={
                              activeCustomLayoutLock ? (
                                <LockOutlined
                                  sx={{ width: "20px", height: "20px" }}
                                />
                              ) : (
                                <LockOpenOutlined
                                  sx={{ width: "20px", height: "20px" }}
                                />
                              )
                            }
                            startIconEnabled={true}
                            buttonStartIcon={
                              activeCustomLayoutLock ? (
                                <LockOutlined
                                  sx={{ width: "20px", height: "20px" }}
                                />
                              ) : (
                                <LockOpenOutlined
                                  sx={{ width: "20px", height: "20px" }}
                                />
                              )
                            }
                            btnLabel={
                              activeCustomLayoutLock ? "Unlock" : "Lock"
                            }
                          />
                        )}
                        <ToolbarBtn
                          width={width}
                          tooltipId="slide-outline-tooltip"
                          tooltipTitle={`Show block options`}
                          onClick={(event) => {
                            setBlockToolsAnchor(event.currentTarget);
                          }}
                          icon={
                            <WidgetsOutlined
                              sx={{ width: "20px", height: "20px" }}
                            />
                          }
                          btnLabel="Block"
                        />

                        <ToolbarBtn
                          width={width}
                          tooltipId="slide-settings-tooltip"
                          tooltipTitle="Slide settings"
                          onClick={() => props.onSlideSettings()}
                          icon={
                            <SettingsOutlinedIcon
                              sx={{ width: "20px", height: "20px" }}
                            />
                          }
                          btnLabel="Settings"
                        />

                        <ToolbarBtn
                          width={width}
                          tooltipId="background-tooltip"
                          tooltipTitle="Slide background"
                          onClick={() => props.onBackgroundSettings()}
                          icon={
                            <BackgroundIcon
                              sx={{ width: "20px", height: "20px" }}
                            />
                          }
                          btnLabel="Background"
                        />

                        <ToolbarBtn
                          width={width}
                          tooltipId="logo-tooltip"
                          tooltipTitle="Slide logo"
                          onClick={() => props.onLogoSettings()}
                          icon={
                            <SvgIcon
                              sx={{
                                width: "20px",
                                height: "20px",
                                fill: "currentcolor",
                              }}
                            >
                              <svg
                                width="1200pt"
                                height="1200pt"
                                version="1.1"
                                viewBox="0 0 1200 1200"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <g>
                                  <path d="m1136.4 537.6-87.602-58.801 46.801-94.801c10.801-22.801 9.6016-48-2.3984-69.602-12-21.602-34.801-36-60-37.199l-105.6-7.1992-7.1992-104.4c-1.1992-25.199-15.602-46.801-37.199-60-21.602-12-48-13.199-69.602-2.3984l-93.598 46.797-58.801-87.602c-27.602-40.801-96-42-123.6 0l-57.598 87.602-94.801-46.801c-22.801-10.801-48-9.6016-69.602 2.3984-21.602 12-36 34.801-37.199 60l-7.1992 105.6-104.4 7.1992c-25.199 1.1992-46.801 15.602-60 37.199-12 21.602-13.199 48-2.3984 70.801l46.801 94.801-87.602 58.801c-20.398 14.398-33.602 37.199-33.602 62.398 0 25.199 12 48 33.602 62.398l87.602 58.801-46.801 94.801c-10.801 22.801-9.6016 49.199 2.3984 69.602 12 21.602 34.801 34.801 60 37.199l105.6 7.1992 7.1992 104.4c1.1992 25.199 15.602 46.801 37.199 60 21.602 12 48 13.199 69.602 2.3984l94.801-46.801 58.801 87.602c14.398 20.398 37.199 33.602 62.398 33.602 25.199 0 48-12 62.398-33.602l58.801-87.602 94.801 46.801c22.801 10.801 48 9.6016 69.602-2.3984 21.602-12 36-34.801 37.199-60l7.1992-105.6 105.6-7.1992c25.199-1.1992 46.801-15.602 60-37.199 12-21.602 13.199-48 2.3984-69.602l-50.402-97.195 87.602-58.801c20.398-14.398 33.602-37.199 33.602-62.398-1.2031-25.199-13.203-48-34.805-61.199zm-148.8 114c-31.199 20.398-42 61.199-25.199 94.801l42 85.199-94.801 6c-37.199 2.3984-67.199 32.398-69.602 69.602l-6 94.801-85.199-42c-33.602-16.801-74.398-6-94.801 25.199l-54 80.398-52.801-79.199c-14.398-21.602-38.398-33.602-62.398-33.602-10.801 0-22.801 2.3984-32.398 7.1992l-85.199 42-7.2031-94.801c-2.3984-37.199-32.398-67.199-69.602-69.602l-94.801-6 42-85.199c16.801-33.602 6-74.398-25.199-94.801l-79.199-52.801 79.199-52.801c31.199-20.398 42-61.199 25.199-94.801l-42-85.199 94.801-6c37.199-2.3984 67.199-32.398 69.602-69.602l6-94.801 85.199 42c33.602 16.801 74.398 6 94.801-25.199l52.801-79.199 52.801 79.199c20.398 31.199 61.199 42 94.801 25.199l85.199-42 6 94.801c2.3984 37.199 32.398 67.199 69.602 69.602l94.801 6-42 85.199c-16.801 33.602-6 74.398 25.199 94.801l79.199 52.801z" />
                                  <path d="m720 548.4c-9.6016-14.398-33.602-14.398-43.199 0l-40.801 60-76.801-111.6c-9.6016-14.398-33.602-14.398-43.199 0l-136.8 199.2c-6 8.3984-6 18-1.1992 27.602 4.8008 8.3984 13.199 14.398 22.801 14.398h397.2c9.6016 0 19.199-4.8008 22.801-14.398 4.8008-8.3984 3.6016-19.199-1.1992-27.602z" />
                                  <path d="m733.2 423.6c0 31.148-25.25 56.398-56.398 56.398-31.148 0-56.402-25.25-56.402-56.398 0-31.152 25.254-56.402 56.402-56.402 31.148 0 56.398 25.25 56.398 56.402" />
                                </g>
                              </svg>
                            </SvgIcon>
                          }
                          btnLabel="Logo"
                        />

                        <ToolbarBtn
                          width={width}
                          tooltipId="transition-tooltip"
                          tooltipTitle="Slide transition"
                          onClick={() => props.onTransitionSettings()}
                          icon={
                            <SvgIcon
                              sx={{
                                width: "24px",
                                height: "24px",
                                fill: "currentColor",
                              }}
                            >
                              <svg
                                viewBox="0 0 447.489 375.571"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M 306.696 161.014 L 202.958 161.014 L 250.207 113.765 L 227.948 91.506 L 153.82 165.634 C 150.872 168.587 149.214 172.591 149.214 176.765 C 149.214 180.938 150.872 184.942 153.82 187.895 L 227.948 262.023 L 250.207 239.764 L 202.958 192.515 L 306.696 192.515 L 306.696 161.014 Z"
                                  style={{
                                    transformBox: "fill-box",
                                    transformOrigin: "50% 50%",
                                  }}
                                  transform="matrix(-1, 0, 0, -1, -0.000024, 0.000005)"
                                />
                                <rect
                                  x="359.018"
                                  y="34.817"
                                  width="31.963"
                                  height="283.676"
                                  transform="matrix(1, 0, 0, 1, -293.949796010571, 0)"
                                />
                                <rect
                                  x="359.018"
                                  y="34.817"
                                  width="31.963"
                                  height="283.676"
                                />
                              </svg>
                            </SvgIcon>
                          }
                          btnLabel="Transitions"
                        />
                      </>
                    )}

                    {presenterAudioEnabled &&
                      permissions.access_audio_features && (
                        <ToolbarBtn
                          btnClassName={hasSlideAudio ? "has-slide-audio" : ""}
                          btnId="slide-audio-setting-button"
                          width={width}
                          tooltipId="audio-tooltip"
                          tooltipTitle="Slide audio"
                          onClick={() => props.onSlideAudio()}
                          icon={
                            <RecordVoiceOverIcon
                              sx={{ width: "20px", height: "20px" }}
                            />
                          }
                          btnLabel="Audio"
                        />
                      )}

                    {presenterNotesEnabled &&
                      permissions.access_note_features && (
                        <ToolbarBtn
                          width={width}
                          tooltipId="notes-tooltip"
                          tooltipTitle="Slide notes"
                          onClick={() => props.onSlideNotes()}
                          icon={
                            <NotesIcon sx={{ width: "20px", height: "20px" }} />
                          }
                          btnLabel="Notes"
                        />
                      )}

                    {!isCorrupt && (
                      <ToolbarBtn
                        width={width}
                        tooltipId="delete-tooltip"
                        tooltipTitle="Delete slide"
                        btnSubVariant={"error"}
                        onClick={() => props.onRemoveSlides([slide.id])}
                        icon={
                          <SvgIcon>
                            <svg
                              style={{
                                fill: "currentColor",
                                fontSize: "0.8rem",
                              }}
                              id="Layer_1"
                              data-name="Layer 1"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 881.19 1069.98"
                            >
                              <path
                                d="M248,1070a166.75,166.75,0,0,1-118.45-49.28l-.07-.07c-31.82-32-49.35-74-49.35-118.52V410.69H46a46.06,46.06,0,0,1-46-46L.53,256.88A122.12,122.12,0,0,1,36.59,170.2l.19-.18a122.17,122.17,0,0,1,86.65-36H246.87V96a96.14,96.14,0,0,1,96-96h196a96.14,96.14,0,0,1,96,96v38H758.29A122.08,122.08,0,0,1,845,170.08l.46.45a122.56,122.56,0,0,1,35.75,86.39l-.53,107.81a46.07,46.07,0,0,1-46,46H800.52v491.4A168.23,168.23,0,0,1,632.65,1070ZM172.11,902.13a75.12,75.12,0,0,0,22.17,53.52l.44.44A75.15,75.15,0,0,0,248,978H632.63a76.26,76.26,0,0,0,75.86-75.86V410.69H172.11ZM788.66,318.68l.52-62a30.43,30.43,0,0,0-9-21.61l-.35-.34a30.53,30.53,0,0,0-21.59-8.79H123.43a30.57,30.57,0,0,0-21.78,9l-.17.17a30.6,30.6,0,0,0-9,21.69L92,318.67ZM562.84,134V96a24.15,24.15,0,0,0-24-24h-196a24.15,24.15,0,0,0-24,24v38Z"
                                transform="translate(0 -0.02)"
                              />
                              <path
                                d="M575.05,844a31.1,31.1,0,0,1-31-31V575.68a31,31,0,1,1,62,0V813A31,31,0,0,1,575.05,844Z"
                                transform="translate(0 -0.02)"
                              />
                              <path
                                d="M295.57,844a31,31,0,0,1-31-31V575.68a31,31,0,0,1,62,0V813A31.09,31.09,0,0,1,295.57,844Z"
                                transform="translate(0 -0.02)"
                              />
                              <path
                                d="M435.3,844a31,31,0,0,1-31-31V575.68a31,31,0,0,1,62,0V813A31.09,31.09,0,0,1,435.3,844Z"
                                transform="translate(0 -0.02)"
                              />
                            </svg>
                          </SvgIcon>
                        }
                        startIconEnabled={true}
                        buttonStartIcon={
                          width < 1400 ? (
                            ""
                          ) : (
                            <SvgIcon>
                              <svg
                                style={{
                                  fill: "currentColor",
                                  fontSize: "0.8rem",
                                }}
                                id="Layer_1"
                                data-name="Layer 1"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 881.19 1069.98"
                              >
                                <path
                                  d="M248,1070a166.75,166.75,0,0,1-118.45-49.28l-.07-.07c-31.82-32-49.35-74-49.35-118.52V410.69H46a46.06,46.06,0,0,1-46-46L.53,256.88A122.12,122.12,0,0,1,36.59,170.2l.19-.18a122.17,122.17,0,0,1,86.65-36H246.87V96a96.14,96.14,0,0,1,96-96h196a96.14,96.14,0,0,1,96,96v38H758.29A122.08,122.08,0,0,1,845,170.08l.46.45a122.56,122.56,0,0,1,35.75,86.39l-.53,107.81a46.07,46.07,0,0,1-46,46H800.52v491.4A168.23,168.23,0,0,1,632.65,1070ZM172.11,902.13a75.12,75.12,0,0,0,22.17,53.52l.44.44A75.15,75.15,0,0,0,248,978H632.63a76.26,76.26,0,0,0,75.86-75.86V410.69H172.11ZM788.66,318.68l.52-62a30.43,30.43,0,0,0-9-21.61l-.35-.34a30.53,30.53,0,0,0-21.59-8.79H123.43a30.57,30.57,0,0,0-21.78,9l-.17.17a30.6,30.6,0,0,0-9,21.69L92,318.67ZM562.84,134V96a24.15,24.15,0,0,0-24-24h-196a24.15,24.15,0,0,0-24,24v38Z"
                                  transform="translate(0 -0.02)"
                                />
                                <path
                                  d="M575.05,844a31.1,31.1,0,0,1-31-31V575.68a31,31,0,1,1,62,0V813A31,31,0,0,1,575.05,844Z"
                                  transform="translate(0 -0.02)"
                                />
                                <path
                                  d="M295.57,844a31,31,0,0,1-31-31V575.68a31,31,0,0,1,62,0V813A31.09,31.09,0,0,1,295.57,844Z"
                                  transform="translate(0 -0.02)"
                                />
                                <path
                                  d="M435.3,844a31,31,0,0,1-31-31V575.68a31,31,0,0,1,62,0V813A31.09,31.09,0,0,1,435.3,844Z"
                                  transform="translate(0 -0.02)"
                                />
                              </svg>
                            </SvgIcon>
                          )
                        }
                        btnLabel="Delete"
                      />
                    )}

                    {!isReadOnly && !isCorrupt && (
                      <Col xs="auto">
                        <Box
                          className={`bx ${width >= 1400 ? "checkbox" : ""}`}
                        >
                          <Tooltip
                            id="slide-visibility-toggle-tooltip"
                            title={
                              slide?.published === true
                                ? "Hide slide"
                                : "Unhide slide"
                            }
                            placement="bottom"
                          >
                            {width >= 1400 ? (
                              <FormControlLabel
                                labelPlacement="end"
                                className="d-flex align-items-center"
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={
                                      slide?.published === true ? false : true
                                    }
                                    sx={{
                                      "& .MuiFormControlLabel-label": {
                                        fontSize: "0.75rem",
                                        fontWeight: 500,
                                        color: "#777",
                                      },
                                    }}
                                    onChange={(event) => {
                                      props.toggleStatus([slide.id]);
                                    }}
                                  />
                                }
                                label={
                                  <Typography
                                    variant="subtitle2"
                                    owner="admin"
                                    color="adminText.dark"
                                    weight="regular"
                                  >
                                    Hide
                                  </Typography>
                                }
                              />
                            ) : (
                              <Button
                                ref={deleteTooltipTarget}
                                variant="toolbar-btn"
                                sub_variant="primary"
                                owner="admin"
                                onClick={() => {
                                  props.toggleStatus([slide.id]);
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  owner="admin"
                                  className="btn-lh"
                                >
                                  {slide?.published === true ? (
                                    <UnhideIcon
                                      sx={{ width: "20px", height: "20px" }}
                                    />
                                  ) : (
                                    <HideIcon
                                      sx={{ width: "20px", height: "20px" }}
                                    />
                                  )}
                                </Typography>
                              </Button>
                            )}
                          </Tooltip>
                        </Box>
                      </Col>
                    )}
                  </Row>
                </Col>
              )}
            </Row>
          </>
        )}
      </Container>
    </div>
  );
}
